import { Plus } from "lucide-react";
import { ShiftCard } from "./ShiftCard";
import { isoDate } from "./shiftUtils";

/**
 * Mobile-only agenda-style roster. Below `md` we can't fit a 7-day
 * grid on a phone, so instead we render each day as a section
 * ("MON · 15"), listing only the sites that have a shift that day.
 * Every ShiftCard remains fully interactive (tap to edit, drag to move
 * is disabled here — mobile users rarely need it). Empty days collapse
 * so a fully-scheduled week doesn't drown in "no shifts" placeholders,
 * but if the whole week is empty we surface an inline hint so the
 * screen isn't ambiguously blank.
 */
export function MobileRosterList({
  days,
  visibleSites,
  totalSitesCount,
  cellMap,
  conflictIds,
  vacationConflictIds,
  shortRest,
  onCellEmptyClick,
  onAddToCell,
  onEditShift,
  onEditSeries,
  onDeleteSeries,
  onDeleteShift,
}) {
  const todayKey = new Date().toDateString();
  // Build day -> [{site, cell}] where cell is non-empty
  const daySections = days.map((d) => {
    const rows = visibleSites
      .map((site) => ({
        site,
        cell: cellMap[`${site.id}|${d.toDateString()}`] || [],
      }))
      .filter((r) => r.cell.length > 0);
    return { day: d, rows };
  });
  const anyShifts = daySections.some((s) => s.rows.length > 0);

  return (
    <div
      data-testid="mobile-roster-list"
      className="md:hidden border border-[#27272A] bg-[#121212]"
    >
      {visibleSites.length === 0 && (
        <div className="p-8 text-center text-zinc-500 scanlines">
          {totalSitesCount === 0 ? "No sites configured." : "No sites match the current filters."}
        </div>
      )}
      {visibleSites.length > 0 && !anyShifts && (
        <div className="p-8 text-center text-zinc-500 scanlines text-sm">
          No shifts scheduled in this window.
        </div>
      )}
      {daySections.map(({ day, rows }) => {
        if (rows.length === 0) return null;
        const isToday = day.toDateString() === todayKey;
        const dateStr = isoDate(day);
        return (
          <div key={day.toISOString()} className="border-b last:border-b-0 border-[#27272A]">
            <div
              className={`px-3 py-2 border-b border-[#27272A] flex items-baseline gap-2 ${
                isToday ? "bg-[#FF3B30]/10" : "bg-[#0A0A0A]"
              }`}
              data-testid={`mobile-roster-day-header-${dateStr}`}
            >
              <span className="label-mute">{day.toLocaleDateString(undefined, { weekday: "short" })}</span>
              <span className="font-mono font-black text-lg tracking-tighter">{day.getDate()}</span>
              <span className="label-mute">{day.toLocaleDateString(undefined, { month: "short" })}</span>
              {isToday && (
                <span className="ml-auto text-[9px] font-mono uppercase tracking-widest bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 px-1.5 py-0.5">
                  Today
                </span>
              )}
            </div>
            <div className="divide-y divide-[#1f1f22]">
              {rows.map(({ site, cell }) => (
                <div key={site.id} className="p-2.5 space-y-1.5">
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <div className="label-mute truncate">{site.client_name}</div>
                      <div className="font-display text-sm uppercase font-bold tracking-tight truncate">{site.name}</div>
                    </div>
                    <button
                      type="button"
                      data-testid={`mobile-add-to-cell-${site.id}-${dateStr}`}
                      onClick={() => onAddToCell(site.id, day)}
                      title="Add shift"
                      className="shrink-0 border border-dashed border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-500 p-1.5"
                    >
                      <Plus className="size-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {cell.map((s) => (
                      <ShiftCard
                        key={s.id}
                        shift={s}
                        isConflict={conflictIds.has(s.id)}
                        isVacationConflict={vacationConflictIds && vacationConflictIds.has(s.id)}
                        shortRestMinutes={shortRest ? shortRest.get(s.id) : undefined}
                        onEdit={onEditShift}
                        onEditSeries={onEditSeries}
                        onDeleteSeries={onDeleteSeries}
                        onDelete={onDeleteShift}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {/* Suppress unused param warning — onCellEmptyClick is not used on
          mobile (each day section only lists sites that already have a
          shift; use the per-site "+" button to add). */}
      {false && onCellEmptyClick && null}
    </div>
  );
}
